import type { TExample } from '@atlas/uicorn/preview/modules';
import * as React from 'react';
import styles from './IconButtonsGroup.module.css';
import cn from 'classnames';
import normalizeCssSize, { type TCssSize } from '@atlas/tool/box/normalizeCssSize';
import IconButton, { type TIconButtonProps } from '@atlas/uicorn/lib/Control/IconButton';
import MoonIcon from '@atlas/uicorn/lib/Icon/MoonIcon';
import SettingIcon from '@atlas/uicorn/lib/Icon/SettingsIcon';
import PersonIcon from '@atlas/uicorn/lib/Icon/PersonIcon';

const IconButtonsGroup = React.memo(function IconButtonsGroup(
    props: TIconButtonsGroupProps,
): React.ReactElement | null {
    const { gap, items, disabled, size, className, style, ...elementProps } = props;

    if (!items.length) return null;

    const inlineStyles: TIconButtonsGroupStyle = { ...style };

    if (gap) {
        inlineStyles['--icon-buttons-group-gap'] = normalizeCssSize(gap);
    }

    return (
        <menu
            {...elementProps}
            className={cn(styles.iconButtonsGroup, className)}
            style={inlineStyles}
            data-ui-role="icon-buttons-group"
        >
            {items.map((item, index) => {
                if (React.isValidElement(item)) {
                    return (
                        <li key={item.key} className={styles.groupItem}>
                            {item}
                        </li>
                    );
                }

                const itemProps = item as Exclude<TIconButtonsGroupItem, React.ReactElement>;

                if ('separator' in itemProps) {
                    return <li key={index} className={styles.groupSeparator} />;
                }

                const { active, key, ...buttonProps } = itemProps;
                const buttonDisabled = disabled || buttonProps.disabled;

                return (
                    <li key={key ?? (itemProps.tooltip as string)} className={styles.groupItem}>
                        <IconButton
                            {...buttonProps}
                            size={size}
                            disabled={buttonDisabled}
                            className={cn(styles.iconButton, {
                                [styles.active]: active,
                            })}
                        />
                    </li>
                );
            })}
        </menu>
    );
});

export default IconButtonsGroup;

// #region Types

export type TIconButtonsGroupItem =
    | (Omit<TIconButtonProps, 'key' | 'tooltip'> & { active?: boolean } & (
              | { tooltip: TIconButtonProps['tooltip']; key: React.Key }
              | { tooltip: string; key?: React.Key }
          ))
    | { separator: true }
    | React.ReactElement;

export type TIconButtonsGroupProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> & {
    items: TIconButtonsGroupItem[];
    gap?: TCssSize | null;
    size?: number;
    disabled?: boolean;
};

export type TIconButtonsGroupStyle = React.CSSProperties & {
    '--icon-buttons-group-gap'?: string;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <IconButtonsGroup
                disabled={props.disabled}
                size={props.size}
                gap={props.gap}
                items={[
                    { tooltip: 'Profile', icon: <PersonIcon />, onClick: () => props.onClick('Profile') },
                    { tooltip: 'Theme', icon: <MoonIcon />, onClick: () => props.onClick('Theme') },
                    { tooltip: 'Settings', icon: <SettingIcon />, onClick: () => props.onClick('Settings') },
                ]}
            />
        );
    },
    controls: {
        disabled: 'boolean',
        size: {
            type: 'number',
            initial: 40,
            connected: false,
        },
        gap: {
            type: 'number',
            initial: 2,
            connected: false,
        },
    },
    handlers: {
        onClick: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=4-850&mode=design&t=03J7uvwFAjeVCe8Y-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('IconButtonsGroup', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
